import UserCard from '@/components/Users/Card'
import ItemPopup from '@/components/MapsV2/Popups/Item'
import TourVisitPopup from '@/components/MapsV2/Popups/TourVisit'
import ShippingPopup from '@/components/MapsV2/Popups/Shipping'
import CompanyAutocomplete from '@/components/Autocompletes/CompanyAutocomplete'
import MapService from '@/services/MapService'

export default {
  computed: {
    mapLayersConfig() {
      return {
        items_self: {
          label: this.ecosystemModules?.items?.label || this.$gettext('Items'),
          visible: true,
          clustered: true,
          layerParams: {
            clustered: true
          },
          endpoint: (appConfig) => `${appConfig.api.baseUrl}gcm/items`,
          params: {
            includes: ['item_tracking', 'shipping.items', 'location', 'last_known_location'],
            fields: 'id,reference_code,description,active,item_tracking.lat,item_tracking.lng,item_tracking.last_position_updated_at,item_tracking.meta,last_known_location.external_id,last_known_location.name,last_known_location.reference_location,location.external_id,location.name,location.reference_location,serial_number,shipping,last_position_updated_at,attributes.name,attributes.value',
            filter_groups: [{
              filters: [
                ['active', 'eq', 1]
              ]
            }]
          },
          filters: {
            layout: [{
              row: [
                {
                  span: 6,
                  keys: ['reference_code', 'description', 'is_loaded']
                },
                {
                  span: 6,
                  keys: ['last_position_updated_at']
                }
              ]
            }],
            items: {
              reference_code: {
                label: this.$gettext('Reference Code'),
                type: 'text',
                filter: (val) => ['reference_code', 'ct', val]
              },
              description: {
                label: this.$gettext('Description'),
                type: 'text',
                filter: (val) => ['description', 'ct', val]
              },
              is_loaded: {
                label: this.$gettext('Is Loaded'),
                type: 'radio',
                options: [
                  {
                    text: this.$gettext('All'),
                    value: ''
                  },
                  {
                    text: this.$gettext('YES'),
                    value: '1'
                  },
                  {
                    text: this.$gettext('NO'),
                    value: '0'
                  }
                ],
                defaultValue: '',
                filter: (val) => [
                  ['attributes.name', 'eq', 'is_loaded'],
                  ['attributeValue', 'eq', val]
                ]
              },
              last_position_updated_at: {
                label: this.$gettext('Last Position Updated by Age'),
                type: 'radio',
                options: [
                  {
                    text: this.$gettext('All'),
                    value: ''
                  },
                  {
                    text: this.$gettext('Last 36 hours'),
                    value: ['0 hours', '36 hours']
                  },
                  {
                    text: this.$gettext('Last 72 hours'),
                    value: ['36 hours', '72 hours']
                  },
                  {
                    text: this.$gettext('More than 72 hours'),
                    value: ['+72 hours']
                  }
                ],
                defaultValue: '',
                stacked: true,
                filter: (val) => {
                  if (val) {
                    const filter = [
                      [
                        'last_position_updated_at',
                        'lt',
                        this.moment()
                          .subtract(val[0].split(' ')[0], val[0].split(' ')[1])
                          .utc()
                          .format('YYYY-MM-DD HH:mm:ss')
                      ]
                    ]
                    if (val[1]) {
                      filter.push([
                        'last_position_updated_at',
                        'gt',
                        this.moment()
                          .subtract(val[1].split(' ')[0], val[1].split(' ')[1])
                          .utc()
                          .format('YYYY-MM-DD HH:mm:ss')
                      ])
                    }
                    return filter
                  } else {
                    return []
                  }
                }
              }
            }
          },
          fetchErrorMsg: this.$gettext('Error loading items'),
          timeout: 20000,
          smartPolling: true,
          isIncremental: true,
          timestampField: 'last_position_updated_at',
          transform: (response) => {
            if (!response.data || !Array.isArray(response.data)) return []

            return response.data
              .filter(item => item.item_tracking?.lat && item.item_tracking?.lng)
              .map(item => {
                const parsedAttributes = {}
                if (item.attributes?.length) {
                  for (const attribute of item.attributes) {
                    parsedAttributes[attribute.name] = attribute.value
                  }
                }

                if (this.ecosystemModules.items.icon.colorByMinAge) {
                  const daysIdle = this.elapsedTimeRaw(item.last_position_updated_at) / 86400000
                  parsedAttributes.itemColor = this.itemColorByAge(daysIdle, 'items')
                }
                parsedAttributes.direction = 0
                if (this.ecosystemModules.items.directionableBy) {
                  parsedAttributes.direction = this.getItemDirection(this.ecosystemModules.items.directionableBy.split('.').reduce((o, i) => o[i], item))
                }

                parsedAttributes.dynamic = null
                try {
                  if (this.ecosystemModules.items.dynamicableBy) {
                    parsedAttributes.dynamic = this.getItemDynamic(this.ecosystemModules.items.dynamicableBy.split('.').reduce((o, i) => o[i], item) || null)
                  }
                } catch (e) {
                  console.log(e)
                }

                parsedAttributes.load = null
                try {
                  if (this.ecosystemModules.items.loadableBy) {
                    parsedAttributes.load = this.ecosystemModules.items.loadableBy.split('.').reduce((o, i) => o[i], item)
                  }
                } catch (e) {
                  console.assert(e)
                }
                item.parsedAttributes = parsedAttributes

                return {
                  id: item.id,
                  lat: Number.parseFloat(item.item_tracking.lat),
                  lng: Number.parseFloat(item.item_tracking.lng),
                  label: item.reference_code || item.id,
                  status: item.active === 1 ? 'active' : 'inactive',
                  parsedAttributes,
                  ...item
                }
              })
          },
          markerRenderer: (item) => {
            item = item.item
            const isLoaded = item.parsedAttributes?.is_loaded === '1'
            const isDynamic = item.parsedAttributes?.dynamic === 'true'
            const color = item.parsedAttributes?.itemColor || '#000'
            const textColor = this.textContrastColor ? this.textContrastColor(color).replace('color: ', '') : '#fff'
            const dynamicColor = isDynamic ? '#0d0' : '#d00'

            return `
                        <div class="map-tag text-nowrap" style="background-color: ${color}; color: ${textColor}; display: inline-flex; align-items: center; gap: 3px;">
                            ${isDynamic ? `<span class="icon-dynamic-mark" style="background: ${dynamicColor}"></span>` : ''}
                            <span>${this.$options.filters.cleanentityprefix(item.reference_code || item.id)}</span>
                            ${isLoaded
    ? '<span class="icon-load-mark" style="width: 8px; height: 8px; border-radius: 2px; background: #0d0; display: inline-block;"></span>'
    : '<span class="icon-load-mark" style="width: 8px; height: 8px; border-radius: 2px; background: #fff; display: inline-flex; align-items: center; justify-content: center; font-size: 10px;"><span style="color: #d00;">×</span></span>'
}
                        </div>
                        `
          },
          markerPopup: (item) => ({
            component: ItemPopup,
            props: {
              item
            }
          })
        },
        tours_self: {
          label: this.$gettext('Tours'),
          visible: true,
          layerParams: {
            type: 'featureGroup',
            pane: 'toursPane',
            zIndex: 410
          },
          endpoint: (appConfig) => `${appConfig.api.baseUrl}tours`,
          params: () => ({
            filter_groups: [{
              filters: [
                ['status', 'in', ['CONFIRMED', 'PICKEDUP', 'INTRANSIT']],
                ['updated_at', 'bt', [
                  this.moment().subtract(2, 'days').startOf('day').utc().format('YYYY-MM-DD HH:mm:ss'),
                  this.moment().endOf('day').utc().format('YYYY-MM-DD HH:mm:ss')
                ]]
              ]
            }],
            includes: ['visits', 'courier']
          }),
          fetchErrorMsg: this.$gettext('Error loading tours'),
          timeout: 130000,
          transform: (response) => {
            if (!response.data || !Array.isArray(response.data)) return []
            return response.data.map(tour => {
              tour.color = this.stringToColour(tour.id)
              return this.realVisits(tour)
            })
          },
          updateMethod: 'updateTours',
          markerPopup: (visit, parent, context) => ({
            component: TourVisitPopup,
            props: {
              visit,
              tour: context.tour
            }
          })
        },
        couriers_self: {
          label: this.$gettext('Couriers'),
          visible: true,
          clustered: false,
          layerParams: {
            // Default layerGroup
          },
          endpoint: (appConfig) => `${appConfig.api.baseUrl}couriers-available`,
          params: () => ({
            includes: ['user', 'tracking', 'user.roles'],
            limit: 500,
            sort: [{
              key: 'updated_at',
              direction: 'desc'
            }],
            filter_groups: [{
              filters: [
                ['active', 'eq', 1],
                ...(!this.ecosystemModules.companies && !this.ecosystemModules.carriers
                  ? [
                      ['user.company_id', 'eq', this.$store.getters.profile.company_id]
                    ]
                  : [])
              ]
            }]
          }),
          fetchErrorMsg: this.$gettext('Error loading couriers'),
          timeout: 30000,
          smartPolling: true,
          isIncremental: false,
          timestampField: 'tracking.updated_at',
          filters: {
            layout: [{
              row: [
                {
                  span: 6,
                  keys: ['id', 'name', 'carrier']
                },
                {
                  span: 6,
                  keys: ['last_position_updated_at']
                }
              ]
            }],
            items: {
              id: {
                label: this.$gettext('ID'),
                type: 'text',
                filter: (val) => ['user_id', 'ct', val]
              },
              name: {
                label: this.$gettext('Name'),
                type: 'text',
                filter: (val) => ['user.name', 'ct', val]
              },
              carrier: {
                label: this.$gettext('Carrier'),
                type: 'custom',
                createComponent: (value) => ({
                  is: CompanyAutocomplete,
                  restrictTo: [['relationship', 'eq', 'provider']],
                  clearable: true,
                  value: value?.name || '',
                  preQuery: value?.name || ''
                }),
                defaultValue: null,
                filter: (val) => {
                  console.log(val)

                  if (val?.id) {
                    return [['user.company_id', 'eq', val.id]]
                  }
                  return []
                }
              },
              last_position_updated_at: {
                label: this.$gettext('Last Position Updated by Age'),
                type: 'radio',
                options: [
                  {
                    text: this.$gettext('All'),
                    value: ''
                  },
                  {
                    text: this.$gettext('Last 36 hours'),
                    value: ['0 hours', '36 hours']
                  },
                  {
                    text: this.$gettext('Last 72 hours'),
                    value: ['36 hours', '72 hours']
                  },
                  {
                    text: this.$gettext('More than 72 hours'),
                    value: ['+72 hours']
                  }
                ],
                defaultValue: '',
                stacked: true,
                filter: (val) => {
                  if (val) {
                    const filter = [
                      [
                        'tracking.updated_at',
                        'lt',
                        this.moment()
                          .subtract(val[0].split(' ')[0], val[0].split(' ')[1])
                          .utc()
                          .format('YYYY-MM-DD HH:mm:ss')
                      ]
                    ]
                    if (val[1]) {
                      filter.push([
                        'tracking.updated_at',
                        'gt',
                        this.moment()
                          .subtract(val[1].split(' ')[0], val[1].split(' ')[1])
                          .utc()
                          .format('YYYY-MM-DD HH:mm:ss')
                      ])
                    }
                    return filter
                  } else {
                    return []
                  }
                }
              }
            }
          },
          transform: (response) => {
            if (!response.data || !Array.isArray(response.data)) return []
            return response.data.map(courier => {
              if (!courier.tracking || courier.tracking.length === 0) return null
              const isFree = courier.tracking[0].shipping_id === null || courier.tracking[0].shipping_id === '' ? 'blue' : 'gray'
              return {
                id: courier.id,
                lat: Number.parseFloat(courier.tracking[0]?.lat || 0),
                lng: Number.parseFloat(courier.tracking[0]?.lng || 0),
                label: courier.user.name || courier.id,
                vehicle_type: 'coche',
                icon: this.$appConfig.staticBaseUrl + 'static/img/ic-map-' + this.vehicleTypes.find(x => x.id === courier.vehicle_type_id).name.toLowerCase() + '-' + isFree + '.svg',
                ...courier,
                user: courier.user
              }
            }).filter(Boolean)
          },
          markerRenderer: (courier) => {
            courier = courier.item
            return {
              html: `<img src="${courier.icon}" style="width: 100%; height: 100%;" />`,
              iconSize: [36, 36],
              iconAnchor: [18, 18]
            }
          },
          markerPopup: (courier) => ({
            component: UserCard,
            props: {
              user: courier.user,
              withMap: false,
              profilePictureSize: '50px',
              withTitles: false
            }
          })
        },
        geofences_self: {
          label: this.$gettext('Geofences'),
          visible: true,
          layerParams: {
            type: 'geoJSON',
            pane: 'geofencesPane',
            zIndex: 350,
            pointerEvents: 'none'
          },
          endpoint: (appConfig) => `${appConfig.api.baseUrl}geo-fences`,
          params: () => {
            const bounds = MapService.getBounds()
            if (!bounds) return null
            return {
              filter_groups: [{
                filters: [
                  ['boundary', 'eq', bounds._southWest.lat + ',' + bounds._southWest.lng + '|' + bounds._northEast.lat + ',' + bounds._northEast.lng]
                ]
              }],
              fields: 'id,external_id,name,fence,geo_type_id,geo_type.level,geo_type.name,geo_type.type,lat,lng,reference_location',
              includes: ['geo_type']
            }
          },
          fetchErrorMsg: this.$gettext('Error loading geofences'),
          timeout: null,
          transform: (response) => {
            const data = response.data || response
            if (!Array.isArray(data)) return null
            return data
              .filter(item => item.fence)
              .map(item => {
                const feature = item.fence
                if (!feature.properties) feature.properties = {}
                feature.properties.name = item.name
                feature.properties.external_id = item.external_id
                feature.properties.geo_type = item.geo_type
                feature.properties.reference_location = item.reference_location
                feature.properties.color = this.stringToColour('11' + item.geo_type.name)
                return feature
              })
          },
          style: (feature) => {
            const color = feature.geometry?.properties?.color || feature.properties?.color || '#3388ff'
            return {
              fillColor: color,
              weight: 1,
              opacity: 1,
              color,
              fillOpacity: 0.4
            }
          },
          markerTooltip: (props) => `${this.$options.filters.cleanentityprefix(props.external_id) || ''} <strong>${this.$options.filters.cleanentityprefix(props.name)} ${props.reference_location ? `(${props.reference_location})` : ''}</strong>`,
          customUpdate: (data, config, layerKey) => {
            MapService.updateGeofences(layerKey, data, config)
          }
        },
        shippings_self: {
          label: this.$gettext('Shippings'),
          visible: true,
          clustered: true,
          layerParams: {
            clustered: true
          },
          endpoint: (appConfig) => `${appConfig.api.baseUrl}shippings`,
          // smartPolling: true,
          // isIncremental: true,
          // timestampField: 'updated_at',
          params: () => ({
            filter_groups: [{
              filters: [
                ['tour_id', 'eq', null],
                ['status', 'in', this.generalMapPreferences?.shippings_self?.statuses?.length ? this.generalMapPreferences?.shippings_self?.statuses : ['READY', 'CONFIRMED', 'PICKEDUP', 'INTRANSIT']],
                ['updated_at', 'bt', [
                  this.moment().subtract(7, 'days').startOf('day').utc().format('YYYY-MM-DD HH:mm:ss'),
                  this.moment().endOf('day').utc().format('YYYY-MM-DD HH:mm:ss')
                ]],
                ['to_country_iso_code', 'eq', this.$store.getters.profile.company.country_iso_code]
              ]
            }],
            includes: this.ecosystemModules.items.self ? ['items', 'threads'] : ['threads']
          }),
          filters: {
            items: {
              status: {
                label: this.$gettext('Status'),
                type: 'checkbox-group',
                options: [
                  {
                    text: this.$gettext('Ready'),
                    value: 'READY'
                  },
                  {
                    text: this.$gettext('Confirmed'),
                    value: 'CONFIRMED'
                  },
                  {
                    text: this.$gettext('Picked Up'),
                    value: 'PICKEDUP'
                  },
                  {
                    text: this.$gettext('In Transit'),
                    value: 'INTRANSIT'
                  },
                  {
                    text: this.$gettext('At Delivery Point'),
                    value: 'ATDELIVERYPOINT'
                  },
                  {
                    text: this.$gettext('Delivered'),
                    value: 'DELIVERED'
                  }
                ],
                filter: (val) => ['status', 'in', val]
              }
            }
          },
          fetchErrorMsg: this.$gettext('Error loading shippings'),
          timeout: 30000,
          transform: (response) => {
            if (!response.data || !Array.isArray(response.data)) return []
            return response.data.map(ship => ({
              ...ship,
              from_lat: ship.address_from?.lat,
              from_lng: ship.address_from?.lng,
              to_lat: ship.address_to?.lat,
              to_lng: ship.address_to?.lng
            }))
          },
          updateMethod: 'updateShippings',
          markerPopup: (shipping, parent, context) => ({
            component: ShippingPopup,
            props: {
              shipping
            }
          })
        },
        shippings_shared: {
          label: this.$gettext('Shared Shippings'),
          visible: true,
          clustered: false,
          endpoint: (appConfig) => `${appConfig.api.baseUrl}gcm/waiting-shippings`,
          params: () => ({
            ...(this.baseData || {}),
            only_shippings: 1,
            showLimit: 50
          }),
          fetchErrorMsg: this.$gettext('Error loading shared shippings'),
          timeout: 15000,
          transform: (response) => {
            const data = response.data || response
            if (!Array.isArray(data)) return []
            return data.map(ship => ({
              ...ship,
              from_lat: ship.from_lat,
              from_lng: ship.from_lng,
              to_lat: ship.to_lat,
              to_lng: ship.to_lng
            }))
          },
          updateMethod: 'updateShippings',
          markerPopup: (shipping) => `
                    <div style="padding: 10px; min-width: 200px;">
                        <h6 style="margin: 0 0 5px 0; color: #007bff;">Shared Shipment</h6>
                        <div style="font-size: 12px;">
                            <div><strong>Status:</strong> ${shipping.status}</div>
                            <hr style="margin: 5px 0;">
                            <div><strong>From:</strong> ${shipping.from_address || 'N/A'}</div>
                            <div><strong>To:</strong> ${shipping.to_address || 'N/A'}</div>
                        </div>
                    </div>
                    `
        }
      }
    }
  }
}
